
/**
 * Guidance Service - Provides helpful suggestions when users make general queries
 * without explicitly mentioning integration names
 * FIXED: Now checks if required keywords are already present before providing guidance
 */

export interface GuidanceResponse {
  isGuidance: true;
  guidanceType: string;
  suggestion: string;
  examples: string[];
  formattedResponse: string;
}

/**
 * Check if query already contains required NAV/document keywords
 */
function hasNavDocumentKeywords(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  
  const navKeywords = [
    'nav omverdenanalyse', 'navs omverdenanalyse', 'nav-omverdenanalyse',
    'nav rapport', 'navs rapport', 'nav-rapport', 
    'nav arbeidsmarkedsanalyse', 'navs arbeidsmarkedsanalyse',
    'omverdenanalyse', 'arbeidsmarkedsanalyse fra nav',
    'hva sier nav', 'ifølge nav', 'nav sier', 'i nav-rapporten',
    'kan du finne i nav', 'søk i nav dokument', 'finn i nav rapport'
  ];
  
  return navKeywords.some(keyword => lowerQuery.includes(keyword));
}

/**
 * Check if query already contains required Enhetsregisteret keywords
 */
function hasEnhetsregisterKeywords(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  
  const enhetsregisterKeywords = [
    'enhetsregisteret', 'enhetsregister', 'enhets-registeret',
    'brønnøysundregisteret', 'brønnøysund-registeret', 'brønnøysund registeret',
    'brønnøysund', 'brreg', 'br reg', 'br-reg',
    'søk enhetsregisteret', 'søke enhetsregisteret', 'finn i enhetsregisteret',
    'søk i enhetsregisteret', 'brønnøysundregistrene', 'bedriftsregisteret'
  ];
  
  return enhetsregisterKeywords.some(keyword => lowerQuery.includes(keyword));
}

/**
 * Check if query already contains required Utdanning.no keywords
 */
function hasUtdanningKeywords(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  
  const utdanningKeywords = [
    'utdanning.no', 'utdanningno', 'utdanning no',
    'søk utdanning.no', 'søke utdanning.no', 'finn på utdanning.no',
    'søk i utdanning.no', 'utdanning.no database', 'kan du søke utdanning.no'
  ];
  
  return utdanningKeywords.some(keyword => lowerQuery.includes(keyword));
}

/**
 * Check if query already contains required Arbeidsplassen keywords
 */
function hasArbeidsplassenKeywords(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  
  const arbeidsplassenKeywords = [
    'arbeidsplassen.no', 'arbeidsplassen', 'nav arbeidsplassen',
    'søk arbeidsplassen', 'søke arbeidsplassen', 'finn på arbeidsplassen',
    'søk i arbeidsplassen', 'arbeidsplassen.no database'
  ];
  
  return arbeidsplassenKeywords.some(keyword => lowerQuery.includes(keyword));
}

/**
 * Generate guidance response for general queries
 * FIXED: Only provide guidance if required keywords are NOT already present
 */
export function generateGuidanceResponse(query: string): GuidanceResponse | null {
  const lowerQuery = query.toLowerCase();
  
  console.log(`[Guidance-Service] 🤔 Generating guidance for: "${query}"`);
  
  // Business queries without explicit Enhetsregisteret mention
  const businessTerms = [
    'bedrifter', 'firmaer', 'firma', 'selskap', 'butikker', 'butikk',
    'it-firmaer', 'it-firma', 'byggfirma', 'snekkerfirma', 'rørleggerfirma'
  ];
  
  const hasBusinessTerms = businessTerms.some(term => lowerQuery.includes(term));
  
  // FIXED: Only provide business guidance if Enhetsregisteret keywords are NOT present
  if (hasBusinessTerms && !hasEnhetsregisterKeywords(query)) {
    console.log(`[Guidance-Service] 🏢 Business terms found but no Enhetsregisteret keywords - providing guidance`);
    return {
      isGuidance: true,
      guidanceType: 'business',
      suggestion: 'For å søke i bedriftsdatabasen, må du spesifisere "Enhetsregisteret" eller "BRREG"',
      examples: [
        'Søk i Enhetsregisteret for IT-firmaer i Oslo',
        'Finn i BRREG snekkerfirmaer på Kongsberg',
        'Søk Enhetsregisteret for restauranter i Bergen'
      ],
      formattedResponse: `## 🏢 Bedriftssøk krever spesifisering

Jeg ser du spør om bedrifter, men for å søke i bedriftsdatabasen må du eksplisitt nevne **Enhetsregisteret** eller **BRREG**.

**Eksempler på korrekte søk:**
- "Søk i Enhetsregisteret for IT-firmaer i Oslo"
- "Finn i BRREG snekkerfirmaer på Kongsberg"  
- "Søk Enhetsregisteret for restauranter i Bergen"

**Hvorfor kreves dette?**
Dette sikrer at du får presise resultater fra riktig database og gir deg kontroll over når API-kall utføres.

Prøv igjen med en av eksemplene ovenfor! 🚀`
    };
  }
  
  // Education queries without explicit Utdanning.no mention
  const educationTerms = [
    'utdanning', 'studium', 'studie', 'bachelor', 'master', 'kurs',
    'utdanninger', 'studier', 'studieprogram'
  ];
  
  const hasEducationTerms = educationTerms.some(term => lowerQuery.includes(term));
  
  // FIXED: Only provide education guidance if Utdanning.no keywords are NOT present
  if (hasEducationTerms && !hasUtdanningKeywords(query)) {
    console.log(`[Guidance-Service] 🎓 Education terms found but no Utdanning.no keywords - providing guidance`);
    return {
      isGuidance: true,
      guidanceType: 'education',
      suggestion: 'For å søke utdanninger, må du spesifisere "Utdanning.no"',
      examples: [
        'Søk på Utdanning.no for bachelor i informatikk',
        'Finn på Utdanning.no master i økonomi',
        'Søk Utdanning.no for sykepleierutdanning'
      ],
      formattedResponse: `## 🎓 Utdanningssøk krever spesifisering

Jeg ser du spør om utdanning, men for å søke i utdanningsdatabasen må du eksplisitt nevne **Utdanning.no**.

**Eksempler på korrekte søk:**
- "Søk på Utdanning.no for bachelor i informatikk"
- "Finn på Utdanning.no master i økonomi"
- "Søk Utdanning.no for sykepleierutdanning"

**Hvorfor kreves dette?**
Dette sikrer at du får presise resultater fra riktig database og gir deg kontroll over når API-kall utføres.

Prøv igjen med en av eksemplene ovenfor! 🚀`
    };
  }
  
  // Job queries without explicit Arbeidsplassen mention
  const jobTerms = [
    'stillinger', 'stilling', 'jobber', 'jobb', 'ledige stillinger',
    'arbeidsplasser', 'karriere'
  ];
  
  const hasJobTerms = jobTerms.some(term => lowerQuery.includes(term));
  
  // FIXED: Only provide job guidance if Arbeidsplassen keywords are NOT present
  if (hasJobTerms && !hasArbeidsplassenKeywords(query)) {
    console.log(`[Guidance-Service] 💼 Job terms found but no Arbeidsplassen keywords - providing guidance`);
    return {
      isGuidance: true,
      guidanceType: 'jobs',
      suggestion: 'For å søke jobber, må du spesifisere "Arbeidsplassen.no"',
      examples: [
        'Søk på Arbeidsplassen.no for utviklerstillinger',
        'Finn på Arbeidsplassen for lærerjobber',
        'Søk Arbeidsplassen.no for deltidsstillinger'
      ],
      formattedResponse: `## 💼 Jobbsøk krever spesifisering

Jeg ser du spør om jobber, men for å søke i jobbdatabasen må du eksplisitt nevne **Arbeidsplassen.no**.

**Eksempler på korrekte søk:**
- "Søk på Arbeidsplassen.no for utviklerstillinger"
- "Finn på Arbeidsplassen for lærerjobber"
- "Søk Arbeidsplassen.no for deltidsstillinger"

**Hvorfor kreves dette?**
Dette sikrer at du får presise resultater fra riktig database og gir deg kontroll over når API-kall utføres.

Prøv igjen med en av eksemplene ovenfor! 🚀`
    };
  }
  
  // Work market analysis without explicit NAV mention
  const workMarketTerms = [
    'arbeidsmarked', 'arbeidsmarkedet', 'jobbmarked', 'arbeidsmarkedsanalyse'
  ];
  
  const hasWorkMarketTerms = workMarketTerms.some(term => lowerQuery.includes(term));
  
  // FIXED: Only provide NAV guidance if NAV keywords are NOT present
  if (hasWorkMarketTerms && !hasNavDocumentKeywords(query)) {
    console.log(`[Guidance-Service] 📊 Work market terms found but no NAV keywords - providing guidance`);
    return {
      isGuidance: true,
      guidanceType: 'documents',
      suggestion: 'For å få informasjon fra NAVs analyser, må du spesifisere "NAV omverdenanalyse"',
      examples: [
        'Hva sier NAVs omverdenanalyse om arbeidsmarkedet?',
        'Finn i NAV rapport om fremtidens jobber',
        'Søk NAV omverdenanalyse for IT-bransjen'
      ],
      formattedResponse: `## 📊 NAV-analyser krever spesifisering

Jeg ser du spør om arbeidsmarkedet, men for å få tilgang til NAVs analyser må du eksplisitt nevne **NAV omverdenanalyse** eller **NAV rapport**.

**Eksempler på korrekte søk:**
- "Hva sier NAVs omverdenanalyse om arbeidsmarkedet?"
- "Finn i NAV rapport om fremtidens jobber"
- "Søk NAV omverdenanalyse for IT-bransjen"

**Hvorfor kreves dette?**
Dette sikrer at du får presise resultater fra riktig database og gir deg kontroll over når API-kall utføres.

Prøv igjen med en av eksemplene ovenfor! 🚀`
    };
  }
  
  console.log(`[Guidance-Service] ✅ No guidance needed for: "${query}" - has required keywords or no conflicting terms`);
  return null;
}

/**
 * Check if a query needs guidance instead of API call
 * FIXED: Now properly checks for existing keywords before suggesting guidance
 */
export function needsGuidance(query: string): boolean {
  const guidance = generateGuidanceResponse(query);
  const needsGuidanceResult = guidance !== null;
  
  console.log(`[Guidance-Service] 🔍 needsGuidance("${query}") = ${needsGuidanceResult}`);
  if (needsGuidanceResult && guidance) {
    console.log(`[Guidance-Service] 📝 Guidance type: ${guidance.guidanceType}`);
  }
  
  return needsGuidanceResult;
}
